import {
  InboundState,
  InboundStatus,
  LotType,
  NoteType,
  OutboundState,
  OutboundStatus,
} from "./constants";
import {
  ensureCompositeKeyFields,
  stripTrackCarFields,
} from "./trackCarPersistence";
import { performUpdateInbound } from "../graphql/inbound/inboundCRUD";
import { performUpdateOutbound } from "../graphql/outbound/outboundCRUD";
import { autogenLotNumber, isAwaitingCarlotNumber } from "./lotNumberUtils";

/**
 * Canonical "the load is spotted" write, shared by every surface that can place
 * a truck: the crew Gate Queue page, and the manager's gate check-in control
 * when a driver is moved to DOCKED. The status/state derivation mirrors the
 * edit forms' save path (inbound DialogSaveButton.setNextState, outbound
 * DialogSaveButton status block) so a shipment placed from the yard floor is
 * indistinguishable from one placed by a manager saving the dispatch tab.
 */

/**
 * Why a placement was refused, in the words every surface says it in.
 *
 * The same refusal reaches the manager from four places — the crew Gate Queue's
 * tooltip, the manager's DOCKED gate control, the dispatch form's Time Placed
 * picker and the track-spotting hooks — and a driver told one thing at the gate
 * and another on the form has no way to tell whether they are two problems.
 *
 * SPOT_NO_INVENTORY is the rail wording and reads mid-sentence, since it is
 * composed into a per-drop notice; the rest are whole sentences.
 */
export const PlacementRefusal = {
  MISSING_GATE_FIELDS:
    "Carrier name and trailer number are required before placing",
  NO_INVENTORY: "Add inventory to the shipment before placing",
  NO_APPOINTMENT:
    "No appointment time — a manager must schedule or place this shipment",
  SPOT_NO_INVENTORY: "add inventory to the shipment first",
};

const placementNote = (text) => ({
  type: NoteType.SHIPMENT,
  text,
  date: new Date().toISOString(),
  by: window.appUser?.fullName,
});

/**
 * A truck shipment needs a carrier name and a trailer number (stored in
 * railCarNumber) before it can be placed. Shared by the crew Gate Queue's Set
 * Placed gate and the manager's DOCKED placement, so neither surface can
 * place a shipment the other would refuse.
 */
export const hasGatePlacementPrereqs = (shipment) =>
  Boolean(
    String(shipment?.carrierName ?? "").trim() &&
      String(shipment?.railCarNumber ?? "").trim(),
  );

/**
 * An inbound shipment can only be placed once it has inventory: placement moves
 * the shipment into PROCESS_SHIPMENT, where the crew works its tally sheet, and
 * with no inventory rows there is nothing to process. SIT is the deliberate
 * exception — a stored-in-transit car is parked as-is and never carries
 * inventory — and SIT is rail-only, so a truck is never exempt.
 *
 * Shared by every inbound placement surface: the manager Inbound List advance
 * button, the dispatch form's Time Placed picker and its DOCKED gate control,
 * both track-management spotting hooks, the crew Gate Queue's Set Placed
 * button, and the gate check-in's DOCKED stamp. None of them may place a
 * shipment another would refuse.
 *
 * An absent inventories field is indistinguishable from an empty one here, so
 * every caller's payload must request it — including the two gate queries
 * (inboundShipmentByStatusGateQueue, getInboundShipmentGatePlacement), which
 * select `inventories { items { id } }` for exactly this check.
 */
export const canPlaceInboundShipment = (shipment) =>
  Boolean(shipment?.sit) || shipment?.inventories?.items?.length > 0;

/**
 * Why a truck cannot be placed through the gate, or null when it can. The one
 * rule every gate surface asks before a driver is moved to DOCKED: the crew
 * Gate Queue's Set Placed button, the manager Gate Queue's status select (at
 * the write, in saveGateCheckIn), and the gate control on both dispatch tabs.
 * DOCKED means the load is spotted, so a shipment that cannot be placed must
 * not have its driver docked either — otherwise the check-in reads DOCKED
 * while the shipment stays ARRIVED, and the crew queue, which shows a button
 * only for a CALLED driver, has nothing left to offer for that truck.
 *
 * Ordered by who has to act: the row's own missing data first (carrier and
 * trailer, which a manager fills in on the shipment), then the inventory rule,
 * which is inbound-only — SIT is exempt but SIT is rail, so no truck qualifies.
 * An absent inventories field reads as empty, so every caller's payload must
 * request `inventories { items { id } }`.
 *
 * @param shipment the shipment (or the dispatch form's values, which carry
 *   the same fields)
 * @param {boolean} outbound the direction
 * @returns {string|null} a PlacementRefusal message, or null
 */
export const gatePlacementRefusal = (shipment, outbound) => {
  if (!hasGatePlacementPrereqs(shipment))
    return PlacementRefusal.MISSING_GATE_FIELDS;
  if (!outbound && !canPlaceInboundShipment(shipment))
    return PlacementRefusal.NO_INVENTORY;
  return null;
};

/**
 * True when this save clears "Time Collected" on a car that had one.
 *
 * Collecting a car — Track Management's pull, or the dispatch form's stamp —
 * sets isCollected and pulls the TrackCar off the board. Clearing the stamp
 * alone used to leave both in place: the shipment said the car was back,
 * billing still read it as collected, and every track view still showed it
 * gone. Callers reset isCollected and restore the TrackCar.
 */
export const isCollectedCleared = (record, data) =>
  Boolean(record?.collectedDateTime) && !data?.collectedDateTime;

/**
 * Statuses a load still sits in before it has been spotted, by direction.
 *
 * Written out once. The track-management drop handler enumerated the inbound
 * three twice more — once as the real gate and once as a cheap pre-check — so a
 * fourth inbound status arriving one day had three places to reach.
 */
export const isPrePlacementInboundStatus = (status) =>
  status === InboundStatus.IN_TRANSIT ||
  status === InboundStatus.ARRIVED ||
  status === InboundStatus.SIT_PENDING;

export const isPrePlacementOutboundStatus = (status) =>
  status === OutboundStatus.PENDING;

/** Statuses a car still sits in before it has been spotted. */
const isPrePlacementInbound = (shipment) =>
  isPrePlacementInboundStatus(shipment?.status);

/**
 * Spotting an inbound car on a track, as both track-management surfaces build
 * it: mutates toUpdate in place and appends the history note that records what
 * actually happened, then reports whether placement was refused.
 *
 * Spotting itself is a physical move and always stands — the TrackCar row is
 * already written by the time this runs. Advancing the shipment to PLACED is
 * separate and can be refused: a non-SIT car with no inventory has nothing for
 * the crew to process, so it keeps its pre-placement status until inventory is
 * added. The note follows the outcome rather than the intent, so a refused
 * placement does not leave an audit trail claiming the car was placed.
 *
 * Shared by the crew's useTrackLocationOperations and the manager's
 * useTrackPlacementUpdate, which held byte-identical copies of this block.
 *
 * @param {object} toUpdate the inbound shipment payload, mutated in place
 * @param {string|null} locationFullName the track the car was spotted on
 * @returns {string|null} the refusal reason, or null when the car was placed
 *   (also null when the car was already past spotting and only moved tracks)
 */
export const applyInboundSpotPlacement = (
  toUpdate,
  { locationFullName } = {},
) => {
  const onTrack = locationFullName ? ` on track ${locationFullName}` : "";
  const refused =
    isPrePlacementInbound(toUpdate) && !canPlaceInboundShipment(toUpdate);

  if (isPrePlacementInbound(toUpdate) && !refused) {
    toUpdate.status = toUpdate.sit
      ? InboundStatus.SIT_PLACED
      : InboundStatus.PLACED;
    toUpdate.state = InboundState.PROCESS_SHIPMENT;
    if (!toUpdate.placedDateTime) {
      const stamp = new Date().toISOString();
      toUpdate.notifyDateTime = toUpdate.notifyDateTime || stamp;
      toUpdate.placedDateTime = stamp;
    }
    if (
      !toUpdate.lotNumberOverride &&
      (toUpdate.lotType === LotType.PERPETUAL ||
        // A carlot template built on {railCarNumber} produced "TBD" while the
        // number was unknown, and placement is where it becomes known — the car
        // number is backfilled from the track car by the caller. Nothing else
        // regenerates it afterwards: placement moves the shipment to
        // PROCESS_SHIPMENT, and the only other carlot regeneration is gated on
        // state NEW. Left alone, the shipment and every inventory row derived
        // from it keep "TBD" permanently.
        isAwaitingCarlotNumber(toUpdate))
    ) {
      toUpdate.lotNumber = autogenLotNumber(
        toUpdate.lotType,
        toUpdate,
        toUpdate.account,
      );
    }
  }

  toUpdate.history = [
    ...(toUpdate.history || []),
    placementNote(
      refused
        ? `Railcar spotted${onTrack} but not placed — no inventory`
        : `Railcar placed${onTrack || " on track"}`,
    ),
  ];

  return refused ? PlacementRefusal.SPOT_NO_INVENTORY : null;
};

/**
 * One notification for a whole drop, however many cars it refused. react-admin
 * queues notifications and plays them one after another, so one notify per car
 * would drip past the manager as a stream of snackbars — a single message that
 * counts the cars and names the first few is readable at a glance.
 */
export const spotRefusalNotice = (refusals) => {
  const cars = refusals.map((entry) => entry?.railCarNumber || "Railcar");
  const shown = cars.slice(0, 5).join(", ");
  const rest = cars.length - 5;
  const reason = refusals[0]?.reason || PlacementRefusal.SPOT_NO_INVENTORY;
  if (cars.length === 1) return `${shown} spotted but not placed — ${reason}.`;
  return `${cars.length} cars spotted but not placed — ${reason}: ${shown}${
    rest > 0 ? ` and ${rest} more` : ""
  }.`;
};

/**
 * Rolls a rejected outbound load back to reworkable, mutating in place the
 * way both crew rejection paths already build their update. Always lands
 * PLACED: the crew process list only queries PLACED, and ARRIVED now means a
 * truck waiting at the gate, so an ARRIVED rollback would strand the load
 * outside the crew list and show it in the Gate Queue as a phantom arrival.
 * A load that reached loading was physically in the yard, so a missing
 * placed time is backfilled from when loading started (or the arrival, or
 * now) rather than inventing a gate arrival. endDateTime is cleared so the
 * dispatch form's next save cannot re-derive LOADED for a rejected load.
 */
export const applyRejectionRollback = (shipment) => {
  shipment.state = OutboundState.REJECTED;
  shipment.status = OutboundStatus.PLACED;
  shipment.endDateTime = null;
  if (!shipment.placementDateTime)
    shipment.placementDateTime =
      shipment.startDateTime ||
      shipment.arrivalDateTime ||
      new Date().toISOString();
  return shipment;
};

/** True when the shipment has already moved past placement — placing again
 * would roll a loaded or released shipment backwards. endDateTime is the work
 * stamp on both sides: the crew write it when the load is finished at a dock,
 * so a legacy row carrying one but no placement stamp was placed in fact.
 * startDateTime is deliberately not tested — no status derivation reads it, it
 * is only ever written to a shipment already placed, and on the inbound side a
 * cleared startDateTime is how the form detects a rollback. The arrival stamps
 * stay out too: inbound notifyDateTime and outbound arrivalDateTime are
 * exactly the truck the gate queue still wants. */
export const isAlreadyPlaced = (shipment, outbound) =>
  outbound
    ? Boolean(
        shipment?.placementDateTime ||
          shipment?.endDateTime ||
          shipment?.departureDateTime,
      )
    : Boolean(
        shipment?.placedDateTime ||
          shipment?.endDateTime ||
          shipment?.releaseDateTime,
      );

/**
 * Places an outbound shipment. The caller supplies a record carrying every
 * composite sort-key member (status, active, endDateTime, arrivalDateTime,
 * scheduledDateTime, createDateTime) — ensureCompositeKeyFields throws if any
 * is missing, since the transformer requires the whole key when status moves.
 *
 * @returns {string} the placement timestamp that was written
 */
export const placeOutboundShipment = async (
  shipment,
  { note = "Placed from gate queue" } = {},
) => {
  const toUpdate = { ...shipment };
  delete toUpdate.direction;
  const stamp = toUpdate.placementDateTime || new Date().toISOString();

  toUpdate.status = OutboundStatus.PLACED;
  toUpdate.placementDateTime = stamp;
  // arrivalDateTime sorts the by-dates GSIs; the gate check-in normally
  // stamped it already, this is the manual-arrival fallback.
  if (!toUpdate.arrivalDateTime) toUpdate.arrivalDateTime = stamp;
  // Same reset the form's save path applies when it derives PLACED.
  toUpdate.state = "";
  toUpdate.daysOnDemurrageClock = 0;
  toUpdate.history = [...(toUpdate.history || []), placementNote(note)];

  const input = stripTrackCarFields(ensureCompositeKeyFields(toUpdate, true));
  await performUpdateOutbound(input);
  return stamp;
};

/**
 * Places an inbound shipment. Regenerates the lot number on the same terms the
 * edit form does — placement is where a PERPETUAL date template and a carlot
 * {railCarNumber} template finally resolve — and moves the shipment into
 * PROCESS_SHIPMENT, which is the axis the crew inbound list actually queries.
 *
 * @returns {string} the placement timestamp that was written
 */
export const placeInboundShipment = async (
  shipment,
  { note = "Placed from gate queue" } = {},
) => {
  const toUpdate = { ...shipment };
  delete toUpdate.direction;
  const stamp = toUpdate.placedDateTime || new Date().toISOString();

  toUpdate.status = toUpdate.sit
    ? InboundStatus.SIT_PLACED
    : InboundStatus.PLACED;
  // Leave an approval state alone; only a shipment still sitting in the NEW
  // lane advances into processing.
  if (!toUpdate.state || toUpdate.state === InboundState.NEW)
    toUpdate.state = InboundState.PROCESS_SHIPMENT;
  if (!toUpdate.placedDateTime) {
    toUpdate.notifyDateTime = toUpdate.notifyDateTime || stamp;
    toUpdate.placedDateTime = stamp;
  }
  if (
    !toUpdate.lotNumberOverride &&
    (toUpdate.lotType === LotType.PERPETUAL ||
      isAwaitingCarlotNumber(toUpdate))
  ) {
    toUpdate.lotNumber = autogenLotNumber(
      toUpdate.lotType,
      toUpdate,
      toUpdate.account,
    );
  }
  toUpdate.history = [...(toUpdate.history || []), placementNote(note)];

  const input = stripTrackCarFields(ensureCompositeKeyFields(toUpdate, false));
  await performUpdateInbound(input);
  return stamp;
};

/** Direction-agnostic placement for callers holding a mixed queue. */
export const placeShipment = (shipment, outbound, options) =>
  outbound
    ? placeOutboundShipment(shipment, options)
    : placeInboundShipment(shipment, options);
